import Vue from "vue"
import { loadStripe } from "@stripe/stripe-js/pure"
import {
  fetchAppliedPromoCodes,
  generatePaymentIntent,
  generateSetupIntent,
  loadUserStripeCards,
  redeemPromoCodes,
  redeemPromoCodeOffer,
  fetchAvailablePromoCodes,
  fetchPromoCodesOffersForBootcamp,
} from "./payment.apis"
import { checkoutWithStripeExactRefill } from "@/api/store"
import { PAYMENT_METHOD } from "./payment.constants"

const state = {
  stripe: null,
  stripeElements: null,
  stripePaymentElement: null,
  paymentLoading: false,
  paymentError: null,
  paymentIntent: null,
  savedPaymentMethods: [],
  currentPaymentTab: PAYMENT_METHOD.STRIPE_SAVED_CARD,
  promoCodeOffers: [],
  availablePromoCodes: [],
  appliedPromoCodes: [],
}

const getters = {
  paymentLoading: state => state.paymentLoading,
  paymentError: state => state.paymentError,
  stripe: state => state.stripe,
  stripeElements: state => state.stripeElements,
  stripePaymentElement: state => state.stripePaymentElement,
  savedPaymentMethods: state => state.savedPaymentMethods,
  currentPaymentTab: state => state.currentPaymentTab,
  promoCodeOffers: state => state.promoCodeOffers,
  availablePromoCodes: state => state.availablePromoCodes,
  appliedPromoCodes: state => state.appliedPromoCodes,
}
const mutations = {
  setStripe: (state, stripe) => (state.stripe = stripe),
  setPaymentLoading: (state, loading) => (state.paymentLoading = loading),
  setPaymentError: (state, paymentError) => (state.paymentError = paymentError),
  setStripeElements: (state, stripeElements) =>
    (state.stripeElements = stripeElements),
  setStripePaymentElement: (state, stripePaymentElement) =>
    (state.stripePaymentElement = stripePaymentElement),
  setPaymentIntent: (state, paymentIntent) =>
    (state.paymentIntent = paymentIntent),
  setSavedPaymentMethods: (state, methods) =>
    (state.savedPaymentMethods = methods),
  setCurrentPaymentTab: (state, currentPaymentTab) =>
    (state.currentPaymentTab = currentPaymentTab),
  setPromoCodeOffers: (state, offers) => (state.promoCodeOffers = offers),
  setAvailablePromoCodes: (state, promoCodes) =>
    (state.availablePromoCodes = promoCodes),
  setAppliedPromoCodes: (state, promoCodes) =>
    (state.appliedPromoCodes = promoCodes),
}
const actions = {
  async getRecaptchaToken ({ commit }) {
    try {
      await Vue.prototype.$recaptchaLoaded()
      return await Vue.prototype.$recaptcha("submit")
    } catch (error) {
      commit("setPaymentError", error?.message || "")
      commit("setPaymentLoading", false)
    }
  },
  async initStripeElements ({ dispatch, state, commit }) {
    commit("setPaymentLoading", true)
    await dispatch("loadStripe")
    try {
      // Initialize Elements
      const stripeElements = state.stripe.elements({
        clientSecret: state.paymentIntent,
      })
      commit("setStripeElements", stripeElements)
      // Set up Payment Element
      const stripePaymentElement = state.stripeElements.create("payment", {
        wallets: {
          applePay: "never",
          googlePay: "never",
        },
      })
      commit("setStripePaymentElement", stripePaymentElement)
    } catch (error) {
      commit("setPaymentError", error.message)
    }
    commit("setPaymentLoading", false)
    // console.info("Stripe Payment Element", state.stripePaymentElement)
  },
  async initStripeElementsForSavedCard ({ dispatch, commit }) {
    await dispatch("loadStripe")
    try {
      // Initialize Elements
      const stripeElements = state.stripe.elements()
      commit("setStripeElements", stripeElements)
    } catch (error) {
      commit("setPaymentError", error.message)
    }
  },
  async loadStripe ({ commit, state }) {
    if (state.stripe) return
    try {
      commit("setStripe", await loadStripe(process.env.STRIPE_PUBLIC_KEY))
    } catch (error) {
      console.error(error)
      commit("setPaymentError", error.message)
      commit("setStripeSubmitting", false)
    }
  },
  async generatePaymentIntent (
    { dispatch, state, commit },
    { productIds, promoCodes, exactRefillProduct }
  ) {
    await dispatch("Util/updateCoverLoading", true, { root: true })
    try {
      commit("setPaymentLoading", true)
      const token = await dispatch("getRecaptchaToken")
      let data

      // handle exactRefillProduct payments
      if (productIds.length === 1 && productIds[0] === "exact-refill-product") {
        data = await checkoutWithStripeExactRefill({
          refillPriceInCents: exactRefillProduct.amount,
          recaptchaToken: token,
          promoCodes,
        })
      } else {
        const body = {
          g_recaptcha_response: token,
          product_ids: productIds,
        }
        if (promoCodes) {
          body.promo_codes = promoCodes
        }
        data = await generatePaymentIntent(body)
      }

      // If invoice_status is paid, then all set
      if (data.invoice_status === "paid") {
        return true
      }
      if (data.status !== "success") {
        commit("setPaymentError", data.message)
        dispatch("Util/updateCoverLoading", false, { root: true })
        return
      }
      commit("setPaymentIntent", data.payment_intent)
      commit("setPaymentLoading", false)
    } catch (error) {
      console.error(error)
    }
    dispatch("Util/updateCoverLoading", false, { root: true })
  },
  async payWithSavedMethod (
    { dispatch, state, commit },
    { productIds, paymentMethodId, cvc, promoCodes, exactRefillProduct }
  ) {
    commit("setPaymentError", null)
    commit("setPaymentLoading", true)
    let data
    const token = await dispatch("getRecaptchaToken")
    // generate token of the cvc_update
    const cvcToken = await state.stripe.createToken("cvc_update", cvc)

    // handle exactRefillProduct payments
    if (productIds.length === 1 && productIds[0] === "exact-refill-product") {
      data = await checkoutWithStripeExactRefill({
        cvcToken,
        paymentMethodId,
        promoCodes,
        recaptchaToken: token,
        refillPriceInCents: exactRefillProduct.amount,
      })
    } else {
      const body = {
        g_recaptcha_response: token,
        product_ids: productIds,
        payment_method_id: paymentMethodId,
        cvc_token: cvcToken?.token?.id,
      }

      if (promoCodes) {
        body.promo_codes = promoCodes
      }

      data = await generatePaymentIntent(body)
    }

    if (data.status !== "success") {
      commit("setPaymentError", data.message)
      commit("setCurrentPaymentTab", PAYMENT_METHOD.STRIPE_SAVED_CARD)
      commit("setPaymentLoading", false)
      return false
    }

    // If invoice_status is paid, then all set
    if (data.invoice_status === "paid") {
      return true
    }

    // checking if the payment is made by a saved payment method
    if (paymentMethodId) {
      const paymentIntent = await state.stripe.retrievePaymentIntent(
        data.payment_intent
      )
      // If payment successfully
      if (paymentIntent.paymentIntent.status === "succeeded") {
        dispatch("Util/updateCoverLoading", false, { root: true })
        return true
      } else {
        // Go stripe payment flow when payment flow failed
        commit("setPaymentIntent", data.payment_intent)
        commit("setCurrentPaymentTab", PAYMENT_METHOD.STRIPE)
        commit("setPaymentError", "Please enter the card information again")
        return false
      }
    }
    commit("setPaymentLoading", false)
  },
  async applyPromoCode ({ dispatch, state, commit }, { productIds, promoCode }) {
    commit("setPromoCode", promoCode)
    commit("setPaymentIntent", null)
    state.stripePaymentElement.unmount()
    await dispatch("generatePaymentIntent", productIds)
    await dispatch("initStripeElements")
    state.stripePaymentElement.mount("#payment-element")
  },
  async payWithStripe ({ state, commit, dispatch }, confirmParams) {
    commit("setPaymentError", null)
    commit("setPaymentLoading", true)
    await dispatch("loadStripe")
    try {
      const result = await state.stripe.confirmPayment({
        elements: state.stripeElements,
        confirmParams,
        redirect: "if_required",
      })
      commit("setPaymentLoading", false)
      if (result.error) {
        commit("setPaymentError", result.error.message)
        return false
      } else {
        commit("setPaymentIntent", null)
        dispatch("Util/updateCoverLoading", false, { root: true })
        return true
      }
    } catch (error) {
      commit("setPaymentError", error)
      commit("setPaymentLoading", false)
      return false
    }
  },
  async loadUserStripeCards ({ commit }) {
    commit("setPaymentLoading", true)
    const data = await loadUserStripeCards()
    commit("setSavedPaymentMethods", data.saved_cards)
    commit("setPaymentLoading", false)
  },

  // TODO: ideally, this should be moved to bootcamp module along with the related state, getters, mutations
  async fetchPromoCodesOffersForBootcamp ({ commit }, bootcampKey) {
    const data = await fetchPromoCodesOffersForBootcamp(bootcampKey)
    if (data.offers) {
      commit("setPromoCodeOffers", data.offers)
    }
  },
  async fetchAppliedPromoCodes ({ commit }) {
    try {
      const data = await fetchAppliedPromoCodes()
      commit("setAppliedPromoCodes", data)
    } catch (e) {
      console.error(e)
    }
  },
  async fetchAvailablePromoCodes ({ commit }) {
    const data = await fetchAvailablePromoCodes()
    commit("setAvailablePromoCodes", data.promo_codes)
  },
  async redeemPromoCodeOffer (context, offerId) {
    await redeemPromoCodeOffer(offerId)
  },
  async redeemPromoCodes ({ dispatch }, promoCode) {
    return redeemPromoCodes(promoCode)
  },
  async generateSetupIntent ({ commit, dispatch }) {
    const token = await dispatch("getRecaptchaToken")
    const body = {
      g_recaptcha_response: token,
    }
    const data = await generateSetupIntent(body)
    if (data.status !== "success") {
      commit("setPaymentError", data.message)
      dispatch("Util/updateCoverLoading", false, { root: true })
      return
    }
    commit("setPaymentIntent", data.setup_intent)
    commit("setPaymentLoading", false)
    dispatch("Util/updateCoverLoading", false, { root: true })
  },
  async setupWithStripe ({ state, commit, dispatch }, confirmParams) {
    commit("setPaymentError", null)
    commit("setPaymentLoading", true)
    await dispatch("loadStripe")
    try {
      const result = await state.stripe.confirmSetup({
        elements: state.stripeElements,
        confirmParams,
        redirect: "if_required",
      })
      commit("setPaymentLoading", false)
      if (result.error) {
        commit("setPaymentError", result.error.message)
        return false
      } else {
        commit("setPaymentIntent", null)
        dispatch("Util/updateCoverLoading", true, { root: true })
        await dispatch("loadUserStripeCards")
        dispatch("Util/updateCoverLoading", false, { root: true })
        return true
      }
    } catch (error) {
      commit("setPaymentError", error)
      commit("setPaymentLoading", false)
      return false
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/payment/payment.module.js